import { useTranslate } from '@refinedev/core';
import { App as AntdApp, Card, Input, InputNumber, Space, Typography } from 'antd';
import { useState } from 'react';
import { SectionDrawer } from '../../components/ToolSection';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import {
  draftComplete,
  draftFromWheel,
  ratingInput,
  type SectorDraft,
  type WheelRow,
} from './model';
import { useValuesCommand } from './useValuesCommand';

/**
 * E36 · "Rate the wheel with the client" as a side drawer (design PORTAL-SECTIONS-2026-10-22 §2.4 (d)1): one row per area — what matters
 * here, now, wanted — starting from the client's last rating. Save is never disabled; pressing it with a mark missing says so, lastingly.
 */
type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  wheel: WheelRow;
  locale: 'en' | 'ru';
  lastRated: string | null;
  onClose: () => void;
  onSaved: () => void;
};

export const RateWheelDrawer = ({
  open,
  clientId,
  clientName,
  wheel,
  locale,
  lastRated,
  onClose,
  onSaved,
}: Props) => {
  const t = useTranslate() as unknown as (k: string, o?: Record<string, unknown>) => string;
  const { message } = AntdApp.useApp();
  const refreshClientPage = useRefreshClientPage();
  const { run, isLoading } = useValuesCommand();
  const opening = draftFromWheel(wheel, locale);
  const [draft, setDraft] = useState<SectorDraft[]>(opening);
  const [error, setError] = useState<string | null>(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(opening);
  const patch = (sectorId: string, change: Partial<SectorDraft>) => {
    setError(null);
    setDraft((rs) => rs.map((r) => (r.sectorId === sectorId ? { ...r, ...change } : r)));
  };
  const close = () => {
    setError(null);
    setDraft(draftFromWheel(wheel, locale));
    onClose();
  };

  const onSave = async () => {
    if (!draftComplete(draft)) {
      setError(t('values.hub.wheel.rate.notComplete'));
      return;
    }
    const res = await run(
      'rateValuesWheelInSession',
      { subject: clientId, wheelId: wheel.wheelId, sectors: ratingInput(draft) },
      t('values.hub.wheel.rate.error'),
    );
    if (res.ok) {
      message.success(t('values.hub.wheel.rate.saved'));
      refreshClientPage('assign');
      setError(null);
      onClose();
      onSaved();
    } else {
      setError(res.message);
    }
  };

  return (
    <SectionDrawer
      open={open}
      title={t('values.hub.wheel.rate.title', { name: clientName })}
      testId="values-hub-rate"
      saveTestId="values-hub-rate-save"
      width="wide"
      context={
        <Space direction="vertical" size={2}>
          <Typography.Text type="secondary">{t('values.hub.wheel.rate.intro')}</Typography.Text>
          {lastRated ? <Typography.Text type="secondary">{lastRated}</Typography.Text> : null}
        </Space>
      }
      saveLabel={t('values.hub.wheel.rate.save')}
      saving={isLoading}
      error={error}
      dirty={dirty}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={close}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {draft.map((r) => (
          <Card key={r.sectorId} size="small" title={r.name} data-testid={`values-hub-rate-sector-${r.sectorId}`}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                data-testid={`values-hub-rate-statement-${r.sectorId}`}
                maxLength={500}
                onChange={(e) => patch(r.sectorId, { valueStatement: e.target.value })}
                placeholder={t('values.hub.wheel.rate.statement')}
                value={r.valueStatement}
              />
              <Space wrap>
                <InputNumber
                  addonBefore={t('values.hub.wheel.rate.now')}
                  data-testid={`values-hub-rate-current-${r.sectorId}`}
                  max={10}
                  min={0}
                  onChange={(v) =>
                    patch(r.sectorId, { current: v === null ? null : Math.round(Number(v)) })
                  }
                  precision={0}
                  value={r.current}
                />
                <InputNumber
                  addonBefore={t('values.hub.wheel.rate.ideal')}
                  data-testid={`values-hub-rate-ideal-${r.sectorId}`}
                  max={10}
                  min={0}
                  onChange={(v) =>
                    patch(r.sectorId, { ideal: v === null ? null : Math.round(Number(v)) })
                  }
                  precision={0}
                  value={r.ideal}
                />
              </Space>
            </Space>
          </Card>
        ))}
      </Space>
    </SectionDrawer>
  );
};
